/** The label a card joins a session with (STORY-089 M4), from the course's card rows
 * (STORY-088's `course_devices`, read by the scan sheet with the teacher's own token).
 *
 * - **A seat row**: the card joins with that seat as its label. Its device id stays
 *   `card:<course_id>:<n>` (`cardDeviceId`), so the session store keeps its person and only the
 *   label changes. A seat is digits, so a guest-only session takes it.
 * - **A course with no seat row** (owner decision, 2026-10-09): every other card joins by its
 *   printed number, as before M4. A card whose row names a student joins by its number too: a
 *   join cannot take a student yet, it has no gateway `subject` until STORY-086. The sheet counts
 *   such cards and never names the student.
 * - **A course with any seat row**: a card with no seat (no row, or a student row) is never
 *   joined (`unmapped`). Seats and printed numbers are the same digits, so spare card 12 joining
 *   as "12" beside the card on seat 12 would name two people the same for the whole session. Its
 *   answer still goes as it stands: the door counts it only for a card already in the session
 *   (a seat since moved, or its number before the course had seats; owner decision, 2026-10-10)
 *   and answers `not_joined` for any other, which counts nothing. The sheet gives the left-out
 *   cards' numbers and asks for seats. The
 *   clicker design treats an unmapped clicker the same way
 *   (`docs/design-clickers-2026-10-03.md`, "an unmapped id").
 *
 * - **An anonymous session** (owner decision, 2026-10-09): the seat rule applies to named sessions
 *   only. The server keeps no label in an anonymous session, so no two cards can share one, and
 *   leaving a card out would only lose its answers: every card joins by its number, whatever the
 *   rows say.
 *
 * A row is found by `device_key === String(n)`: the printed number without leading zeros, the
 * key STORY-088 stores for a card and the `<n>` of the card's device id. */

/** The part of a `course_devices` row this reads. */
export type CardRow = { channel: string; device_key: string; seat?: string | null; subject?: string | null };

export type CardJoin = { label: string; by: "seat" | "number" | "student" } | { label: null; by: "unmapped" };

const seatOf = (r: CardRow): string | null => (r.channel === "card" && r.seat != null && r.seat !== "" ? r.seat : null);

/** Whether the course maps any card to a seat: then a card with no seat is left out. */
export const seatsInUse = (rows: readonly CardRow[]): boolean => rows.some((r) => seatOf(r) !== null);

const optionalText = (v: unknown): boolean => v === undefined || v === null || typeof v === "string";

/** The rows of a read, or null when the reply is not a list of rows: a list holding anything but
 * a row (an object with a string `channel` and `device_key`, and a seat and subject that are
 * text or nothing) is not one, so foreign objects never read as "no seat rows". */
export function asCardRows(value: unknown): CardRow[] | null {
  if (!Array.isArray(value)) return null;
  const ok = value.every((r: unknown) => {
    if (!r || typeof r !== "object") return false;
    const o = r as Record<string, unknown>;
    return typeof o.channel === "string" && typeof o.device_key === "string" && optionalText(o.seat) && optionalText(o.subject);
  });
  return ok ? (value as CardRow[]) : null;
}

export function cardJoinFor(rows: readonly CardRow[], n: number, session: { anonymous?: boolean } = {}): CardJoin {
  const key = String(n);
  if (session.anonymous) return { label: key, by: "number" };
  const row = rows.find((r) => r.channel === "card" && r.device_key === key);
  const seat = row ? seatOf(row) : null;
  if (seat !== null) return { label: seat, by: "seat" };
  if (seatsInUse(rows)) return { label: null, by: "unmapped" };
  if (row?.subject) return { label: key, by: "student" };
  return { label: key, by: "number" };
}
